import React, { useState, useRef } from 'react';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import Select from '../Select/Select';
import styles from './ImportDialog.module.css';
import Checkbox from '../Checkbox/Checkbox';

const ImportDialog = ({
    show,
    onClose,
    onImport,
    loading = false,
    supportedFormats = ['GeoJSON', 'KML', 'SHP', 'CSV'],
    availableLayers = ['plots', 'buildings', 'roads', 'markers']
}) => {
    const [file, setFile] = useState(null);
    const [targetLayer, setTargetLayer] = useState(availableLayers[0]);
    const [importMode, setImportMode] = useState('append');
    const [validate, setValidate] = useState(true);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const formatOptions = supportedFormats.map(f => ({ value: f, label: f }));
    const layerOptions = availableLayers.map(l => ({ value: l, label: l }));

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError(null);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            setError(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleImport = () => {
        if (!file) {
            setError('Выберите файл для импорта');
            return;
        }

        if (onImport) {
            onImport({
                file,
                targetLayer,
                importMode,
                validate
            });
        }
    };

    const handleClose = () => {
        setFile(null);
        setError(null);
        setTargetLayer(availableLayers[0]);
        setImportMode('append');
        setValidate(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const isImportDisabled = !file || loading;

    return (
        <Modal
            show={show}
            onClose={handleClose}
            title="Импорт данных"
            size="md"
            footer={
                <>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleImport}
                        loading={loading}
                        disabled={isImportDisabled}
                    >
                        Импорт
                    </Button>
                </>
            }
        >
            <div className={styles.content}>
                {/* Выбор файла */}
                <div className={styles.field}>
                    <label className={styles.label}>Файл для импорта</label>
                    <div
                        className={`${styles.dropZone} ${file ? styles.hasFile : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={supportedFormats.map(f => `.${f.toLowerCase()}`).join(',')}
                            onChange={handleFileChange}
                            className={styles.fileInput}
                        />
                        {file ? (
                            <div className={styles.fileInfo}>
                                <span className={styles.fileIcon}>📄</span>
                                <div className={styles.fileDetails}>
                                    <span className={styles.fileName}>{file.name}</span>
                                    <span className={styles.fileSize}>
                                        {(file.size / 1024).toFixed(1)} КБ
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.dropZoneText}>
                                <span className={styles.dropIcon}>📁</span>
                                <span>Перетащите файл сюда или нажмите для выбора</span>
                                <span className={styles.dropFormats}>
                                    Поддерживаемые форматы: {supportedFormats.join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Целевой слой */}
                <div className={styles.field}>
                    <label className={styles.label}>Импортировать в слой</label>
                    <Select
                        options={layerOptions}
                        value={targetLayer}
                        onChange={setTargetLayer}
                    />
                </div>

                {/* Режим импорта */}
                <div className={styles.field}>
                    <label className={styles.label}>Режим импорта</label>
                    <div className={styles.radioGroup}>
                        <div className={styles.radioOption}>
                            <Select
                                options={[
                                    { value: 'append', label: 'Добавить к существующим' },
                                    { value: 'replace', label: 'Заменить существующие' },
                                    { value: 'update', label: 'Обновить по совпадению' }
                                ]}
                                value={importMode}
                                onChange={setImportMode}
                            />
                        </div>
                    </div>
                </div>

                {/* Опции */}
                <div className={styles.field}>
                    <div className={styles.optionsGroup}>
                        <Checkbox
                            id="validate-data"
                            label="Валидировать данные перед импортом"
                            checked={validate}
                            onChange={setValidate}
                        />
                    </div>
                </div>

                {/* Ошибка */}
                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                {/* Информация */}
                <div className={styles.info}>
                    <p>
                        Импортированные объекты будут добавлены в слой <strong>{targetLayer}</strong>.
                        {validate && ' Данные будут проверены на корректность.'}
                    </p>
                </div>
            </div>
        </Modal>
    );
};

export default ImportDialog;
